import { SEMANTIC_COLOR } from '@/shared/design-tokens/design-tokens';

export const ASSET_TYPES = {
  DATABASE: 'database',
  DATABASE_SCHEMA: 'databaseSchema',
  TABLE: 'table',
  TOPIC: 'topic',
  DASHBOARD: 'dashboard',
  PIPELINE: 'pipeline',
  MLMODEL: 'mlmodel',
  CONTAINER: 'container',
  API_ENDPOINT: 'apiEndpoint',
  API_COLLECTION: 'apiCollection',
  GLOSSARY_TERM: 'glossaryTerm',
  TAG: 'tag',
  DOMAIN: 'domain',
  SEARCH_INDEX: 'searchIndex',
  PAGE: 'page',
  SERVICE: 'service',
  EXPERIMENT: 'experiment',
  RUN: 'run',
  FEATURE_SET: 'feature_set',
  DATASET: 'dataset',
  PARAMETER_SET: 'parameter_set',
  ARTIFACT: 'artifact',
} as const;

export type AssetType = (typeof ASSET_TYPES)[keyof typeof ASSET_TYPES];

export type AssetTypeKey = keyof typeof ASSET_TYPES;

export const ASSET_TYPE_PLURALS = {
  TABLE: 'tables',
  CONTAINER: 'containers',
  TOPIC: 'topics',
  DATABASE: 'databases',
  DATABASE_SCHEMA: 'databaseSchemas',
  TAG: 'tags',
  GLOSSARY_TERM: 'glossaryTerms',
  SERVICE: 'services',
} as const;

/**
 * 유효한 자산 타입 Set (O(1) 조회 성능)
 */
const VALID_ASSET_TYPES = new Set(Object.values(ASSET_TYPES));

/**
 * entityType이 유효한 자산 타입인지 확인
 *
 * @description
 * entityType이 ASSET_TYPES의 값 중 하나인지 O(1) 성능으로 확인합니다.
 * camelCase 형식의 entityType을 직접 검증합니다.
 *
 * @param entityType - 검증할 entityType (예: 'databaseSchema', 'table')
 * @returns 유효한 자산 타입이면 true
 *
 * @example
 * isValidAssetType('databaseSchema'); // true
 * isValidAssetType('table'); // true
 * isValidAssetType('unknown'); // false
 */
export const isValidAssetType = (entityType: string): boolean => {
  return VALID_ASSET_TYPES.has(entityType as (typeof ASSET_TYPES)[keyof typeof ASSET_TYPES]);
};

export const NODE_TYPES = {
  ENTITY_TYPE: 'entityType',
  SERVICE_TYPE: 'serviceType',
  SERVICE: 'service',
  DATABASE: 'database',
  SCHEMA: 'schema',
  TABLE: 'table',
  TOPIC: 'topic',
} as const;

export type NodeType = (typeof NODE_TYPES)[keyof typeof NODE_TYPES];

export const ENTITY_TYPES = {
  MLMODEL: 'mlmodel',
  CONTAINER: 'container',
  DATABASE: 'database',
  TOPIC: 'topic',
  SCHEMA: 'schema',
  DOMAIN: 'domain',
  DATA_PRODUCT: 'dataProduct',
  DATABASE_SERVICE: 'databaseService',
  MESSAGING_SERVICE: 'messagingService',
  PIPELINE_SERVICE: 'pipelineService',
  STORAGE_SERVICE: 'storageService',
  MLMODEL_SERVICE: 'mlmodelService',
} as const;

export type EntityType = (typeof ENTITY_TYPES)[keyof typeof ENTITY_TYPES];

export const SERVICE_ENTITY_TYPES = [
  ENTITY_TYPES.DATABASE_SERVICE,
  ENTITY_TYPES.MESSAGING_SERVICE,
  ENTITY_TYPES.PIPELINE_SERVICE,
  ENTITY_TYPES.STORAGE_SERVICE,
  ENTITY_TYPES.MLMODEL_SERVICE,
] as const;

export const AGGREGATION_FIELDS = {
  SERVICE_TYPE: 'sterms#serviceType',
  SERVICE_NAME: 'sterms#service.displayName.keyword',
  DATABASE_NAME: 'sterms#database.displayName',
  SCHEMA_NAME: 'sterms#databaseSchema.displayName',
  ENTITY_TYPE: 'sterms#entityType',
  TAGS: 'sterms#tags.tagFQN',
  TIER: 'sterms#tier.tagFQN',
  OWNER: 'sterms#owners.displayName.keyword',
  CERTIFICATION: 'sterms#certification.tagLabel.tagFQN',
} as const;

export type AssetTypeConfig = {
  name: string;
  color: string;
  tagColor: string;
  tagBackgroundColor?: string;
  searchFilter: string;
};

export const ASSET_TYPE_MAP: Record<string, AssetTypeConfig> = {
  table: {
    name: 'Table',
    color: SEMANTIC_COLOR.ON_CONTAINER.COLORFUL.MAGENTA,
    tagColor: SEMANTIC_COLOR.ON_CONTAINER.COLORFUL.MAGENTA,
    tagBackgroundColor: SEMANTIC_COLOR.CONTAINER.COLORFUL.MAGENTA.LEVEL1,
    searchFilter: 'table',
  },
  databaseSchema: {
    name: 'Database Schema',
    color: SEMANTIC_COLOR.ON_CONTAINER.COLORFUL.FUCHSIA,
    tagColor: SEMANTIC_COLOR.ON_CONTAINER.COLORFUL.FUCHSIA,
    tagBackgroundColor: SEMANTIC_COLOR.CONTAINER.COLORFUL.FUCHSIA.LEVEL1,
    searchFilter: 'databaseSchema',
  },
  topic: {
    name: 'Topic',
    color: SEMANTIC_COLOR.ON_CONTAINER.COLORFUL.TURQUOISE,
    tagColor: SEMANTIC_COLOR.ON_CONTAINER.COLORFUL.TURQUOISE,
    tagBackgroundColor: SEMANTIC_COLOR.CONTAINER.COLORFUL.TURQUOISE.LEVEL1,
    searchFilter: 'topic',
  },
  database: {
    name: 'Database',
    color: SEMANTIC_COLOR.ON_CONTAINER.COLORFUL.OLIVE,
    tagColor: SEMANTIC_COLOR.ON_CONTAINER.COLORFUL.OLIVE,
    tagBackgroundColor: SEMANTIC_COLOR.CONTAINER.COLORFUL.OLIVE.LEVEL1,
    searchFilter: 'database',
  },
  container: {
    name: 'Bucket',
    color: SEMANTIC_COLOR.ON_CONTAINER.COLORFUL.TURQUOISE,
    tagColor: SEMANTIC_COLOR.ON_CONTAINER.COLORFUL.TURQUOISE,
    tagBackgroundColor: SEMANTIC_COLOR.CONTAINER.COLORFUL.TURQUOISE.LEVEL1,
    searchFilter: 'container',
  },
  mlmodel: {
    name: 'ML Model',
    color: SEMANTIC_COLOR.ON_CONTAINER.COLORFUL.YELLOW,
    tagColor: SEMANTIC_COLOR.ON_CONTAINER.COLORFUL.YELLOW,
    tagBackgroundColor: SEMANTIC_COLOR.CONTAINER.COLORFUL.YELLOW.LEVEL1,
    searchFilter: 'mlmodel',
  },
  dashboard: {
    name: 'Dashboard',
    color: SEMANTIC_COLOR.ON_CONTAINER.COLORFUL.BLUE,
    tagColor: SEMANTIC_COLOR.ON_CONTAINER.COLORFUL.BLUE,
    tagBackgroundColor: SEMANTIC_COLOR.CONTAINER.COLORFUL.BLUE.LEVEL1,
    searchFilter: 'dashboard',
  },
  pipeline: {
    name: 'Pipeline',
    color: SEMANTIC_COLOR.ON_CONTAINER.COLORFUL.FUCHSIA,
    tagColor: SEMANTIC_COLOR.ON_CONTAINER.COLORFUL.FUCHSIA,
    tagBackgroundColor: SEMANTIC_COLOR.CONTAINER.COLORFUL.FUCHSIA.LEVEL1,
    searchFilter: 'pipeline',
  },
  glossaryTerm: {
    name: 'Glossary Term',
    color: SEMANTIC_COLOR.ON_CONTAINER.COLORFUL.ORANGE,
    tagColor: SEMANTIC_COLOR.ON_CONTAINER.COLORFUL.ORANGE,
    tagBackgroundColor: SEMANTIC_COLOR.CONTAINER.COLORFUL.ORANGE.LEVEL1,
    searchFilter: 'glossaryTerm',
  },
  glossary: {
    name: 'Glossary Term',
    color: SEMANTIC_COLOR.ON_CONTAINER.COLORFUL.ORANGE,
    tagColor: SEMANTIC_COLOR.ON_CONTAINER.COLORFUL.ORANGE,
    tagBackgroundColor: SEMANTIC_COLOR.CONTAINER.COLORFUL.ORANGE.LEVEL1,
    searchFilter: 'glossaryTerm',
  },
  tag: {
    name: 'Tag',
    color: SEMANTIC_COLOR.ON_CONTAINER.COLORFUL.BLUE,
    tagColor: SEMANTIC_COLOR.ON_CONTAINER.COLORFUL.BLUE,
    tagBackgroundColor: SEMANTIC_COLOR.CONTAINER.COLORFUL.BLUE.LEVEL1,
    searchFilter: 'tag',
  },
  experiment: {
    name: 'Experiment',
    color: SEMANTIC_COLOR.ON_CONTAINER.COLORFUL.INDIGO,
    tagColor: SEMANTIC_COLOR.ON_CONTAINER.COLORFUL.INDIGO,
    tagBackgroundColor: SEMANTIC_COLOR.CONTAINER.COLORFUL.INDIGO.LEVEL1,
    searchFilter: 'experiment',
  },
  run: {
    name: 'Run',
    color: SEMANTIC_COLOR.ON_CONTAINER.COLORFUL.PURPLE,
    tagColor: SEMANTIC_COLOR.ON_CONTAINER.COLORFUL.PURPLE,
    tagBackgroundColor: SEMANTIC_COLOR.CONTAINER.COLORFUL.PURPLE.LEVEL1,
    searchFilter: 'run',
  },
  feature_set: {
    name: 'Feature Set',
    color: SEMANTIC_COLOR.ON_CONTAINER.COLORFUL.CHARTREUSE,
    tagColor: SEMANTIC_COLOR.ON_CONTAINER.COLORFUL.CHARTREUSE,
    tagBackgroundColor: SEMANTIC_COLOR.CONTAINER.COLORFUL.CHARTREUSE.LEVEL1,
    searchFilter: 'feature_set',
  },
  dataset: {
    name: 'Dataset',
    color: SEMANTIC_COLOR.ON_CONTAINER.COLORFUL.YELLOW,
    tagColor: SEMANTIC_COLOR.ON_CONTAINER.COLORFUL.YELLOW,
    tagBackgroundColor: SEMANTIC_COLOR.CONTAINER.COLORFUL.YELLOW.LEVEL1,
    searchFilter: 'dataset',
  },
  parameter_set: {
    name: 'Parameter Set',
    color: SEMANTIC_COLOR.ON_CONTAINER.COLORFUL.RED,
    tagColor: SEMANTIC_COLOR.ON_CONTAINER.COLORFUL.RED,
    tagBackgroundColor: SEMANTIC_COLOR.CONTAINER.COLORFUL.RED.LEVEL1,
    searchFilter: 'parameter_set',
  },
  artifact: {
    name: 'Artifact',
    color: SEMANTIC_COLOR.ON_CONTAINER.COLORFUL.GREEN,
    tagColor: SEMANTIC_COLOR.ON_CONTAINER.COLORFUL.GREEN,
    tagBackgroundColor: SEMANTIC_COLOR.CONTAINER.COLORFUL.GREEN.LEVEL1,
    searchFilter: 'artifact',
  },
} as const;

export const getAssetTypeConfig = (type: string): AssetTypeConfig => {
  return (
    ASSET_TYPE_MAP[type] || {
      name: type,
      color: SEMANTIC_COLOR.CONTAINER.COLORFUL.GRAY.LEVEL2,
      tagColor: 'default',
      searchFilter: type,
    }
  );
};

export const getAssetTypeTagColor = (type: string): string => {
  return getAssetTypeConfig(type).tagColor;
};

export const getAssetTypeTagStyle = (
  type: string
): { color?: string; backgroundColor?: string; borderColor?: string } | null => {
  const config = getAssetTypeConfig(type);
  if (config.tagBackgroundColor) {
    return {
      color: config.tagColor,
      backgroundColor: config.tagBackgroundColor,
      borderColor: config.tagBackgroundColor,
    };
  }
  return null;
};

export const getAssetTypeChartColor = (type: string): string => {
  return getAssetTypeConfig(type).color;
};

export const getAssetTypeName = (type: string): string => {
  return getAssetTypeConfig(type).name;
};

export const getAssetTypeSearchFilter = (type: string): string => {
  return getAssetTypeConfig(type).searchFilter;
};

export const formatServiceTypeDisplayName = (serviceType: string): string => {
  if (!serviceType) return '';

  const lower = serviceType.toLowerCase();

  if (lower === 's3') return 'S3';
  if (lower === 'mysql') return 'MySQL';
  if (lower === 'postgresql' || lower === 'postgres') return 'PostgreSQL';
  if (lower === 'mongodb') return 'MongoDB';
  if (lower === 'mlflow') return 'MLflow';

  return serviceType.charAt(0).toUpperCase() + serviceType.slice(1).toLowerCase();
};

export const SERVICE_ASSET_TYPE_MAP: Record<string, string[]> = {
  postgres: [ASSET_TYPES.TABLE, ASSET_TYPES.DATABASE, ASSET_TYPES.DATABASE_SCHEMA],
  mysql: [ASSET_TYPES.TABLE, ASSET_TYPES.DATABASE, ASSET_TYPES.DATABASE_SCHEMA],
  iceberg: [ASSET_TYPES.TABLE, ASSET_TYPES.DATABASE, ASSET_TYPES.DATABASE_SCHEMA],
  s3: [ASSET_TYPES.CONTAINER],
  kafka: [ASSET_TYPES.TOPIC],
  mlflow: [ASSET_TYPES.MLMODEL],
  airflow: [ASSET_TYPES.PIPELINE],
  superset: [ASSET_TYPES.DASHBOARD],
  tableau: [ASSET_TYPES.DASHBOARD],
  powerbi: [ASSET_TYPES.DASHBOARD],
} as const;

export const ENTITY_TYPE_TO_SERVICE_TYPE_MAP: Record<string, string> = {
  [ENTITY_TYPES.DATABASE]: ENTITY_TYPES.DATABASE_SERVICE,
  [ENTITY_TYPES.MLMODEL]: ENTITY_TYPES.MLMODEL_SERVICE,
  [ENTITY_TYPES.CONTAINER]: ENTITY_TYPES.STORAGE_SERVICE,
  [ENTITY_TYPES.TOPIC]: ENTITY_TYPES.MESSAGING_SERVICE,
} as const;

export const SERVICE_TYPE_TO_ENTITY_TYPE_MAP: Record<string, string> = {
  postgres: ENTITY_TYPES.DATABASE_SERVICE,
  postgresql: ENTITY_TYPES.DATABASE_SERVICE,
  mysql: ENTITY_TYPES.DATABASE_SERVICE,
  iceberg: ENTITY_TYPES.DATABASE_SERVICE,
  redshift: ENTITY_TYPES.DATABASE_SERVICE,
  snowflake: ENTITY_TYPES.DATABASE_SERVICE,
  bigquery: ENTITY_TYPES.DATABASE_SERVICE,
  mongodb: ENTITY_TYPES.DATABASE_SERVICE,
  kafka: ENTITY_TYPES.MESSAGING_SERVICE,
  s3: ENTITY_TYPES.STORAGE_SERVICE,
  ceph: ENTITY_TYPES.STORAGE_SERVICE,
  mlflow: ENTITY_TYPES.MLMODEL_SERVICE,
  airflow: ENTITY_TYPES.PIPELINE_SERVICE,
} as const;

export const mapServiceTypeToEntityType = (serviceType: string): string | null => {
  const lowerType = serviceType.toLowerCase();

  if (ENTITY_TYPE_TO_SERVICE_TYPE_MAP[lowerType]) {
    return ENTITY_TYPE_TO_SERVICE_TYPE_MAP[lowerType];
  }

  return SERVICE_TYPE_TO_ENTITY_TYPE_MAP[lowerType] || null;
};

export const QUERY_FIELD_NAMES = {
  SERVICE_TYPE: 'serviceType',
  SERVICE_DISPLAY_NAME: 'service.displayName.keyword',
  SERVICE_NAME: 'service.name.keyword',
  DATABASE_DISPLAY_NAME: 'database.displayName',
  DATABASE_NAME: 'database.name.keyword',
  DATABASE_SCHEMA_DISPLAY_NAME: 'databaseSchema.displayName',
  DATABASE_SCHEMA_NAME: 'databaseSchema.name.keyword',
  ENTITY_TYPE: 'entityType',
  OWNERS_DISPLAY_NAME: 'owners.displayName.keyword',
  TAGS_FQN: 'tags.tagFQN',
  TIER_FQN: 'tier.tagFQN',
  CERTIFICATION_FQN: 'certification.tagLabel.tagFQN',
} as const;

export const FILTER_GROUP_TYPES = {
  DATA_SOURCE: 'dataSource',
  ASSET_TYPE: 'assetType',
  DOMAIN: 'domain',
  OWNER: 'owner',
  TIER: 'tier',
  TAGS: 'tags',
  CERTIFICATION: 'certification',
} as const;

export type FilterGroupType = (typeof FILTER_GROUP_TYPES)[keyof typeof FILTER_GROUP_TYPES];

export const CERTIFICATION_COLORS: Record<string, string> = {
  Gold: SEMANTIC_COLOR.CONTAINER.COLORFUL.YELLOW.LEVEL4,
  Silver: SEMANTIC_COLOR.CONTAINER.COLORFUL.GRAY.LEVEL3,
  Bronze: SEMANTIC_COLOR.ON_CONTAINER.COLORFUL.YELLOW,
} as const;

export const CERTIFICATION_ORDER = ['Gold', 'Silver', 'Bronze'] as const;

export const WHITE_COLOR = '#ffffff' as const;

export const TIER_CONFIG = {
  DEFAULT_PREFIX: 'Tier',
  NUMBERS: [1, 2, 3, 4, 5],
} as const;

export const MLFLOW_SERVICE = {
  id: 'mlflow-service',
  type: 'mlflow',
  name: 'MLflow',
  displayName: 'MLflow',
  description: 'MLflow Experiment Tracking',
} as const;
